import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  LogOut, 
  RefreshCw, 
  Mail, 
  Phone, 
  MapPin, 
  MessageSquare,
  ClipboardList,
  CheckCircle,
  Clock,
  AlertCircle,
  User,
  Calendar,
  X,
  Image
} from 'lucide-react';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    new: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, label: 'New' },
    contacted: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Contacted' },
    completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' }
  };

  const config = statusConfig[status] || statusConfig.new;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
      <Icon className="w-4 h-4 mr-1" />
      {config.label}
    </span>
  );
};

const SubmissionCard = ({ submission, type, onStatusChange, onDelete }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setIsUpdating(true);
    await onStatusChange(submission._id, newStatus, type);
    setIsUpdating(false);
  };

  const handleDelete = async () => {
    setIsUpdating(true);
    await onDelete(submission._id, type);
    setIsUpdating(false);
    setShowDeleteConfirm(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow relative">
      {/* Delete button - X in corner */}
      <button
        onClick={() => setShowDeleteConfirm(true)}
        className="absolute top-3 right-3 w-6 h-6 rounded-full bg-gray-100 hover:bg-red-100 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors"
        title="Delete submission"
        data-testid={`delete-btn-${submission._id}`}
      >
        <X className="w-4 h-4" />
      </button>

      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-white/95 rounded-xl flex flex-col items-center justify-center z-10 p-4">
          <p className="text-gray-700 font-medium mb-4 text-center">Delete this submission?</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={isUpdating}
            >
              {isUpdating ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-start mb-4 pr-6">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${type === 'contact' ? 'bg-primary/10' : 'bg-purple-100'}`}>
            {type === 'contact' ? (
              <MessageSquare className="w-5 h-5 text-primary" />
            ) : (
              <ClipboardList className="w-5 h-5 text-purple-600" />
            )}
          </div>
          <div>
            <h3 className="font-bold text-gray-900">
              {submission.name || 'Quiz Submission'}
            </h3>
            <p className="text-sm text-gray-500 flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              {formatDate(submission.created_at || submission.timestamp)}
            </p>
          </div>
        </div>
        <StatusBadge status={submission.status || 'new'} />
      </div>

      <div className="space-y-2 mb-4">
        {submission.email && (
          <a href={`mailto:${submission.email}`} className="flex items-center text-gray-600 hover:text-primary">
            <Mail className="w-4 h-4 mr-2 text-gray-400" />
            {submission.email}
          </a>
        )}
        {submission.phone && (
          <a href={`tel:${submission.phone}`} className="flex items-center text-gray-600 hover:text-primary">
            <Phone className="w-4 h-4 mr-2 text-gray-400" />
            {submission.phone}
          </a>
        )}
        {submission.address && (
          <p className="flex items-center text-gray-600">
            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
            {submission.address}
          </p>
        )}
      </div>

      {submission.message && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <p className="text-gray-700 text-sm">{submission.message}</p>
        </div>
      )}

      {type === 'quiz' && submission.score !== undefined && (
        <div className="bg-purple-50 rounded-lg p-3 mb-4">
          <p className="text-purple-800 font-medium">Score: {submission.score}</p>
          {submission.recommendation && (
            <p className="text-purple-600 text-sm mt-1">{submission.recommendation}</p>
          )}
        </div>
      )}

      {/* Status Actions */}
      <div className="flex gap-2 pt-4 border-t border-gray-100">
        <Button
          size="sm"
          variant={submission.status === 'new' ? 'default' : 'outline'}
          onClick={() => handleStatusChange('new')}
          disabled={isUpdating}
          className={submission.status === 'new' ? 'bg-blue-600' : ''}
        >
          New
        </Button>
        <Button
          size="sm"
          variant={submission.status === 'contacted' ? 'default' : 'outline'}
          onClick={() => handleStatusChange('contacted')}
          disabled={isUpdating}
          className={submission.status === 'contacted' ? 'bg-yellow-600' : ''}
        >
          Contacted
        </Button>
        <Button
          size="sm"
          variant={submission.status === 'completed' ? 'default' : 'outline'}
          onClick={() => handleStatusChange('completed')}
          disabled={isUpdating}
          className={submission.status === 'completed' ? 'bg-green-600' : ''}
        >
          Completed
        </Button>
      </div>
    </div>
  );
};

export const AdminDashboardPage = () => {
  const [submissions, setSubmissions] = useState({ contacts: [], quizzes: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  const fetchSubmissions = useCallback(async () => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        sessionStorage.removeItem('adminToken');
        navigate('/admin');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSubmissions(data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch submissions');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleStatusChange = async (id, newStatus, type) => {
    const token = sessionStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus, type })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Status updated to ${newStatus}`);
        fetchSubmissions();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id, type) => {
    const token = sessionStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/${id}?type=${type}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Submission deleted');
        fetchSubmissions();
      } else {
        toast.error('Failed to delete submission');
      }
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const getFilteredSubmissions = () => {
    let contacts = submissions.contacts || [];
    let quizzes = submissions.quizzes || [];

    if (filterStatus !== 'all') {
      contacts = contacts.filter(s => (s.status || 'new') === filterStatus);
      quizzes = quizzes.filter(s => (s.status || 'new') === filterStatus);
    }

    if (activeTab === 'contacts') return { contacts, quizzes: [] };
    if (activeTab === 'quizzes') return { contacts: [], quizzes };
    return { contacts, quizzes };
  };

  const filtered = getFilteredSubmissions();
  const totalNew = [...(submissions.contacts || []), ...(submissions.quizzes || [])]
    .filter(s => !s.status || s.status === 'new').length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
            {totalNew > 0 && (
              <span className="bg-red-500 text-white text-sm px-3 py-1 rounded-full">
                {totalNew} new
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/photos')}
            >
              <Image className="w-4 h-4 mr-2" />
              Manage Photos
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchSubmissions}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-gray-500 text-sm">Total Contacts</p>
            <p className="text-2xl font-bold text-gray-900">{submissions.contacts?.length || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-gray-500 text-sm">Quiz Submissions</p>
            <p className="text-2xl font-bold text-gray-900">{submissions.quizzes?.length || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-gray-500 text-sm">New Leads</p>
            <p className="text-2xl font-bold text-blue-600">{totalNew}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-gray-500 text-sm">Completed</p>
            <p className="text-2xl font-bold text-green-600">
              {[...(submissions.contacts || []), ...(submissions.quizzes || [])]
                .filter(s => s.status === 'completed').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2">
              <Button
                variant={activeTab === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('all')}
              >
                All
              </Button>
              <Button
                variant={activeTab === 'contacts' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('contacts')}
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Contacts
              </Button>
              <Button
                variant={activeTab === 'quizzes' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('quizzes')}
              >
                <ClipboardList className="w-4 h-4 mr-1" />
                Quizzes
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('all')}
              >
                All Status
              </Button>
              <Button
                variant={filterStatus === 'new' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('new')}
                className={filterStatus === 'new' ? 'bg-blue-600' : ''}
              >
                New
              </Button>
              <Button
                variant={filterStatus === 'contacted' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('contacted')}
                className={filterStatus === 'contacted' ? 'bg-yellow-600' : ''}
              >
                Contacted
              </Button>
              <Button
                variant={filterStatus === 'completed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('completed')}
                className={filterStatus === 'completed' ? 'bg-green-600' : ''}
              >
                Completed
              </Button>
            </div>
          </div>
        </div>

        {/* Submissions List */}
        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="text-gray-500 mt-4">Loading submissions...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.contacts.map((submission) => (
              <SubmissionCard
                key={`contact-${submission._id}`}
                submission={submission}
                type="contact"
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
            {filtered.quizzes.map((submission) => (
              <SubmissionCard
                key={`quiz-${submission._id}`}
                submission={submission}
                type="quiz"
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
            {filtered.contacts.length === 0 && filtered.quizzes.length === 0 && (
              <div className="col-span-full text-center py-12">
                <User className="w-12 h-12 mx-auto text-gray-300" />
                <p className="text-gray-500 mt-4">No submissions found</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
